import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ArrowUpwardRounded, DescriptionOutlined } from "@mui/icons-material";
import { InsightMark } from "../../../Chat/InsightMark";
import { GREEN, greenText, hairline, textPrimary, textSecondary } from "../steps/stepStyles";

/**
 * Scripted playback of an Insight AI answer, for the onboarding's AI beats.
 *
 * Deliberately NOT a faithful replica of the in-app chat: this is a marketing
 * surface, so it shows the one thing that differentiates Insight (answers
 * grounded in real earnings calls, with sources) and nothing else. The app's
 * real activity strip and branded engine cards were cut — at this size they
 * filled the frame with chrome and buried the answer.
 *
 * Frameless by design. It sits directly on the aurora with only a whisper of
 * surface behind it, so it reads as part of the page rather than a screenshot
 * pasted onto it.
 */

export interface DemoConversation {
  question: string;
  answer: string;
  /** Carried once by the caption rather than repeated on every chip: at
      375-393pt (SE, mini, iPhone 15/16) four chips with "Q2 ’26" overflow and
      wrap 3+1. Quarter-only keeps them on one row on every supported iPhone. */
  sourcesYear: string;
  sources: { ticker: string; quarter: string }[];
}

// TODO(ben): replace both conversations with responses captured from the real
// app for these exact questions before release (honesty rule — no invented
// capabilities). THE SOURCE QUARTERS BELOW ARE ILLUSTRATIVE except where
// noted — swap them for the real calls each captured answer cites.

/** Drafted from public coverage of these companies' directed-energy programs;
    nLIGHT's $627M JLWS award is the July 2026 Business Wire release. */
export const LASER_DEFENCE_DEMO: DemoConversation = {
  question: "Are there any companies making laser weapons to counter drones?",
  answer:
    "Yes. RTX and Lockheed Martin run directed-energy programmes for drone defence, AeroVironment entered laser weapons by acquiring BlueHalo, and nLIGHT recently won a $627M Joint Laser Weapon System contract.",
  sourcesYear: "2026",
  sources: [
    { ticker: "RTX", quarter: "Q2" },
    { ticker: "LMT", quarter: "Q2" },
    { ticker: "AVAV", quarter: "Q1" },
    { ticker: "LASR", quarter: "Q2" },
  ],
};

/** Every claim here is drawn from Aurora Innovation's Q1 2026 call (6 May
    2026): the Q2 launch on International LT trucks, Urmson calling it
    "really imminent" and "predominantly testing a validation at this point",
    the "50-plus percent reduction in Aurora Driver hardware costs", and
    "more than 200 driverless trucks in operation" by year end. The source
    quarter is real for this one, not illustrative. */
export const AURORA_TRUCKS_DEMO: DemoConversation = {
  question: "When are Aurora Innovation going to launch their generation 2 trucks?",
  answer:
    "Q2 2026. Urmson called it imminent on the Q1 call, with the kit still in validation. It ships on International LT trucks, cuts hardware cost by over 50%, and takes the fleet past 200 driverless trucks by year end.",
  sourcesYear: "2026",
  sources: [{ ticker: "AUR", quarter: "Q1" }],
};

/** Corpus floor from useCorpusStats' fallback — stale-low only understates us. */
const CALLS = 34844;

/** The models actually behind the assistant, via OpenRouter. */
const MODELS = [
  { name: "Gemini", src: "/model-logos/gemini.webp" },
  { name: "Claude", src: "/model-logos/claude.webp" },
];

const TYPE_MS = 32;
const SENT_PAUSE_MS = 420;
const THINKING_MS = 1400;
const STREAM_WORD_MS = 48;
const HOLD_MS = 4200;

type Phase = "typing" | "asking" | "thinking" | "streaming" | "done";

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const TALL_SCREEN = "@media (min-height: 780px)";

interface AiChatDemoProps {
  /** Shorter thread for screens that also carry a headline and CTAs. */
  compact?: boolean;
  /** Which scripted exchange to play. Both onboarding AI beats share this
      component so the demo can never drift between them. */
  conversation?: DemoConversation;
}

export const AiChatDemo = ({
  compact = false,
  conversation = LASER_DEFENCE_DEMO,
}: AiChatDemoProps) => {
  const { question, answer, sourcesYear, sources } = conversation;
  const answerWords = useMemo(() => answer.split(" "), [answer]);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [phase, setPhase] = useState<Phase>("typing");
  const [typedChars, setTypedChars] = useState(0);
  const [streamedWords, setStreamedWords] = useState(0);
  // Once an answer has landed it stays on screen while the composer retypes,
  // so the loop never flashes an empty panel between cycles.
  const [hasAnswered, setHasAnswered] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const reduced = useRef(prefersReducedMotion());

  // Keep the caret in view exactly as a real input does: while the text fits
  // scrollLeft clamps to 0 and it stays left-aligned; once it overflows the
  // field scrolls to follow the caret.
  useEffect(() => {
    const field = fieldRef.current;
    if (field) field.scrollLeft = field.scrollWidth;
  }, [typedChars, phase]);

  useEffect(() => {
    if (reduced.current) {
      setPhase("done");
      setTypedChars(question.length);
      setStreamedWords(answerWords.length);
      setHasAnswered(true);
      return;
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (reduced.current) return;
    if (phase === "typing") {
      if (typedChars < question.length) {
        timer.current = setTimeout(() => setTypedChars((count) => count + 1), TYPE_MS);
      } else {
        timer.current = setTimeout(() => {
          setStreamedWords(0);
          setPhase("asking");
        }, SENT_PAUSE_MS);
      }
      return;
    }
    if (phase === "asking") {
      timer.current = setTimeout(() => setPhase("thinking"), SENT_PAUSE_MS);
      return;
    }
    if (phase === "thinking") {
      timer.current = setTimeout(() => setPhase("streaming"), THINKING_MS);
      return;
    }
    if (phase === "streaming") {
      if (streamedWords < answerWords.length) {
        timer.current = setTimeout(
          () => setStreamedWords((count) => count + 1),
          STREAM_WORD_MS,
        );
      } else {
        setHasAnswered(true);
        setPhase("done");
      }
      return;
    }
    // Loop, so the demo still reads as live if the user lingers.
    timer.current = setTimeout(() => {
      setTypedChars(0);
      setPhase("typing");
    }, HOLD_MS);
  }, [phase, typedChars, streamedWords]);

  const isTyping = phase === "typing";
  // During a retype the previous exchange stays put, frozen complete.
  const holdingPrevious = isTyping && hasAnswered;
  const showThread = !isTyping || hasAnswered;
  const showAnswer = holdingPrevious || phase === "streaming" || phase === "done";
  const wordsShown = holdingPrevious ? answerWords.length : streamedWords;

  return (
    <Box
      sx={{
        width: "100%",
        // Wider than the old frame so the left/right conversation asymmetry
        // has room to read; still fluid, so narrow devices just get less.
        maxWidth: 380,
        // Load-bearing. Every step that hosts this drops it into a flex box,
        // and a flex item defaults to `min-width: auto` — its automatic
        // minimum is its MIN-CONTENT width, which in CSS beats max-width. The
        // composer's nowrap text therefore dragged the whole panel wider as it
        // typed, past both maxWidth and the container. Pinning min-width to 0
        // makes maxWidth authoritative, so the panel is always
        // min(container, 380) and never reflows while typing.
        minWidth: 0,
        // Also load-bearing, and NOT redundant with minWidth above. minWidth
        // only stops THIS box being pushed; it does nothing about the box's
        // min-content leaking upward. The hosting steps wrap this in further
        // flex items that default to `min-width: auto`, so the composer's
        // nowrap text was inflating an ANCESTOR, which then dragged the panel
        // wider with it. Making the panel a scroll container zeroes its
        // min-content contribution, so no wrapper can be pushed by what's
        // inside here. Nothing is meant to escape the panel anyway.
        overflow: "hidden",
        mx: "auto",
        borderRadius: "24px",
        // A whisper of surface — enough to group the thread, not enough to
        // draw an edge. No border, no shadow (flat UI).
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.025)" : "rgba(0, 0, 0, 0.02)",
        // The steps that host this centre their text; a chat thread reads as
        // fake when the assistant's reply is centred, so reset it here.
        textAlign: "left",
        px: 2,
        pt: 2,
        pb: 1.5,
        "@keyframes demoDot": {
          "0%, 80%, 100%": { opacity: 0.2 },
          "40%": { opacity: 0.9 },
        },
        "@keyframes demoRise": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "none" },
        },
        "@keyframes demoCaret": {
          "0%, 49%": { opacity: 1 },
          "50%, 100%": { opacity: 0 },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" },
        },
      }}
    >
      {/* Fixed height so the demo never grows and pushes the CTA down.
          Top-aligned: the question anchors under the top edge and the answer
          flows down from it, so nothing jumps as words stream in. */}
      <Box
        sx={{
          // Must clear the question bubble, the provenance line, a 5-line
          // reply AND the single row of source chips — clipping the sources would
          // cut the one element that proves the answer is checkable.
          // Sized against the NARROWEST supported phone (375pt), where the
          // reply wraps to the most lines — measured at 288px there, so this
          // carries a little margin. Wider phones simply have slack.
          height: compact ? 296 : 300,
          ...(compact ? { [TALL_SCREEN]: { height: 300 } } : {}),
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: 1,
          overflow: "hidden",
        }}
      >
        {showThread && (
          <Box
            sx={{
              alignSelf: "flex-end",
              maxWidth: "84%",
              px: 1.75,
              py: 1,
              borderRadius: "16px 16px 5px 16px",
              backgroundColor: GREEN,
              animation: "demoRise 0.3s ease backwards",
            }}
          >
            <Typography
              sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "#ffffff", lineHeight: 1.45 }}
            >
              {question}
            </Typography>
          </Box>
        )}

        {/* Minimal thinking indicator — three dots, no card, no label. */}
        {phase === "thinking" && (
          <Box sx={{ display: "flex", gap: 0.6, alignSelf: "flex-start", py: 1, pl: 0.5 }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: textSecondary(isDark),
                  animation: `demoDot 1.3s ease-in-out ${dot * 0.16}s infinite`,
                }}
              />
            ))}
          </Box>
        )}

        {showAnswer && wordsShown > 0 && (
          <Box
            sx={{
              // Hugs the left edge and stops short of the right, so the thread
              // reads as a two-sided conversation rather than a centred block.
              alignSelf: "flex-start",
              maxWidth: "94%",
              minWidth: 0,
              animation: "demoRise 0.3s ease backwards",
            }}
          >
            {/* Mark and provenance sit ABOVE the reply rather than beside it,
                so the bubble starts hard against the left edge instead of
                being indented by an avatar column — it reads further from the
                user's right-hand bubble, and the reply gains that width for
                its text and source chips. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Box sx={{ flexShrink: 0, display: "flex" }}>
                <InsightMark size={20} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 500,
                  color: textSecondary(isDark),
                }}
              >
                Searched{" "}
                <Box component="span" sx={{ color: GREEN, fontVariantNumeric: "tabular-nums" }}>
                  {CALLS.toLocaleString()}
                </Box>{" "}
                earnings calls
              </Typography>
            </Box>

            <Box sx={{ minWidth: 0 }}>

              {/* An actual reply surface, so it reads as the assistant
                  answering rather than loose copy on the panel. */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "5px 16px 16px 16px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.055)" : "rgba(0, 0, 0, 0.04)",
                }}
              >
                <Typography
                  sx={{ fontSize: "0.8125rem", lineHeight: 1.55, color: textPrimary(isDark) }}
                >
                  {answerWords.slice(0, wordsShown).join(" ")}
                </Typography>

                {/* Sources — the proof you can check the answer yourself.
                    Styled as tappable chips; inert here, real in the app. */}
                {wordsShown === answerWords.length && (
                  <Box
                    sx={{
                      mt: 1.25,
                      pt: 1,
                      borderTop: `1px solid ${hairline(isDark)}`,
                      animation: "demoRise 0.3s ease backwards",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.875 }}>
                      <DescriptionOutlined
                        sx={{ fontSize: 11, color: textSecondary(isDark) }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.5625rem",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: textSecondary(isDark),
                        }}
                      >
                        Sources · {sourcesYear}
                      </Typography>
                    </Box>

                    {/* Green-accented so they read as the one interactive
                        thing in the reply, and quarter-stamped so it's
                        obvious they point at a specific earnings call.
                        Sized to fit all four on ONE row at the narrowest
                        phone width — a 3+1 wrap looked like a mistake. The
                        outward arrow was dropped to buy that width: at 9px it
                        was barely legible, and the green pill already reads
                        as the tappable element. flexWrap stays as a safety
                        net for anything narrower still. */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {sources.map((source) => (
                        <Box
                          key={source.ticker}
                          sx={{
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: 0.375,
                            px: 0.625,
                            py: 0.3,
                            borderRadius: "7px",
                            border: `1px solid ${
                              isDark ? "rgba(34, 197, 94, 0.38)" : "rgba(34, 197, 94, 0.42)"
                            }`,
                            backgroundColor: isDark
                              ? "rgba(34, 197, 94, 0.12)"
                              : "rgba(34, 197, 94, 0.09)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.5625rem",
                              fontWeight: 700,
                              letterSpacing: "0.02em",
                              color: greenText(isDark),
                              whiteSpace: "nowrap",
                            }}
                          >
                            {source.ticker}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.5rem",
                              fontWeight: 500,
                              color: textSecondary(isDark),
                              whiteSpace: "nowrap",
                            }}
                          >
                            {source.quarter}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Powered by — the credibility line, borrowed from the pattern users
          already recognise on AI products. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          pt: 1.75,
          pb: 1.5,
        }}
      >
        {/* Caption above, marks below — same micro-caption idiom as the
            sources label, so the two credibility lines read as one system. */}
        <Typography
          sx={{
            fontSize: "0.5625rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: textSecondary(isDark),
          }}
        >
          Powered by
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.75 }}>
          {MODELS.map((model) => (
            <Box key={model.name} sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box
                component="img"
                src={model.src}
                alt=""
                aria-hidden
                sx={{ width: 15, height: 15, objectFit: "contain", display: "block" }}
              />
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: textPrimary(isDark),
                  opacity: 0.8,
                }}
              >
                {model.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Composer — filled, not outlined, so it reads as part of the panel. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: 1.75,
          pr: 0.75,
          py: 0.875,
          borderRadius: "999px",
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.6)",
          border: `1px solid ${hairline(isDark)}`,
        }}
      >
        {/* A real text field: the text is LEFT-anchored and simply sits there
            until it outgrows the field, at which point the field scrolls to
            keep the caret at the right edge. Driven by scrollLeft rather than
            by right-aligning the row — right-alignment made short strings
            slide in from the left, which no input does. */}
        <Box
          ref={fieldRef}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            sx={{
              display: "inline-block",
              whiteSpace: "nowrap",
              fontSize: "0.75rem",
              lineHeight: 1.5,
              color: isTyping ? textPrimary(isDark) : textSecondary(isDark),
            }}
          >
            {isTyping && question.slice(0, typedChars)}
            {!isTyping && "Ask about any stock…"}
            {isTyping && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: "1.5px",
                  height: "0.9em",
                  ml: "2px",
                  verticalAlign: "text-bottom",
                  backgroundColor: GREEN,
                  animation: "demoCaret 1s step-end infinite",
                }}
              />
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: GREEN,
            flexShrink: 0,
          }}
        >
          <ArrowUpwardRounded sx={{ fontSize: 16, color: "#ffffff" }} />
        </Box>
      </Box>
    </Box>
  );
};
